import React, { useState, useRef, useEffect } from 'react';
import URLInput from '../components/URLInput';
import { summarizeUrl } from '../services/api';
import { useTranslation } from 'react-i18next';
import ResultItem from '../components/ResultItem';

// Componente para mostrar el esqueleto de carga
const ResultSkeleton = () => {
  const { t } = useTranslation();
  return (
    <div className="flex w-full max-w-3xl mx-auto mb-6 animate-pulse">
      <div className="w-8 h-8 mt-0.5 mr-4 bg-gray-200 rounded-full flex-shrink-0"></div>
      <div className="flex-1 bg-gray-100 rounded-2xl px-5 py-4">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"></div>
        <div className="h-3 bg-gray-200 rounded w-1/2 mb-4"></div>
        <div className="space-y-2">
          <div className="h-3.5 bg-gray-200 rounded w-full"></div>
          <div className="h-3.5 bg-gray-200 rounded w-5/6"></div>
        </div>
        <div className="mt-4 text-sm text-gray-500">
          {t('homepage.processing')}
        </div>
      </div>
    </div>
  );
};

const HomePage = () => {
  const { t } = useTranslation();
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialView, setIsInitialView] = useState(true);
  const [isInputMoving, setIsInputMoving] = useState(false);
  const messagesEndRef = useRef(null);
  const resultsContainerRef = useRef(null);

  // Scroll to bottom when new results appear
  useEffect(() => {
    if (results.length > 0 && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [results.length]);

  // Function to mark animation as complete for a result
  const handleAnimationComplete = (resultId) => {
    console.log(`[HomePage] Animation complete for result ID: ${resultId}`);
    setResults(prevResults =>
      prevResults.map(r =>
        r.id === resultId ? { ...r, isNew: false } : r
      )
    );
  };

  // Submit URL function (Refactored with async/await)
  const handleSubmit = async (url) => {
    setIsLoading(true);
    setError(null);
    console.log(`[HomePage] Submitting URL: ${url}`);

    try {
      const data = await summarizeUrl(url);
      console.log("[HomePage] Data received from backend:", data);
      
      const newResult = {
        id: data.news_id || Date.now(), // Use news_id if available, fallback to timestamp
        url: url,
        summary: data.summary,
        status: data.status || 'success',
        message: data.message,
        isNew: true // Mark as new for animation
      };
      console.log("[HomePage] Setting new result (isNew=true):", newResult);
      setResults(prevResults => [...prevResults, newResult]);

    } catch (error) {
      console.error("[HomePage] Error during URL summarization:", error);
      setError(error.message || t('homepage.error'));
    } finally {
      console.log("[HomePage] Setting isLoading to false.");
      setIsLoading(false);
    }
  };

  const handleURLSubmit = async (url) => {
    if (isInitialView) {
      // Iniciar la transición de movimiento
      setIsInputMoving(true);
      setIsInitialView(false);
      
      // Pequeña pausa para permitir la animación de transición
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    
    // Submit directly (sin debounce para mayor fiabilidad)
    handleSubmit(url);
  };

  const hasResults = results.length > 0 || isLoading;

  return (
    <div className={`flex flex-col ${hasResults ? 'justify-between min-h-screen pb-32' : 'justify-center min-h-[calc(100vh-130px)]'}`}>
      {/* Área de mensajes/resultados */}
      {(hasResults || isInputMoving) ? (
        <div 
          ref={resultsContainerRef}
          className="flex-1 w-full overflow-y-auto pt-16 pb-6 transition-opacity duration-500 ease-in-out opacity-100"
        >
          <div className="flex flex-col space-y-6 max-w-3xl mx-auto px-4">
            {results.map(result => {
              // console.log(`[HomePage] Rendering ResultItem for ID: ${result.id}, isNew: ${result.isNew}`); // Optional log here
              return (
                <ResultItem 
                  key={result.id} 
                  result={result}
                  onAnimationComplete={handleAnimationComplete} 
                />
              );
            })}
            
            {/* Skeleton loader durante la carga */}
            {isLoading && <ResultSkeleton />}
            
            {/* Referencia para scroll automático */}
            <div ref={messagesEndRef} />
          </div>
        </div>
      ) : (
        <div className="mx-auto flex flex-col items-center max-w-3xl px-4 gap-2">
          <h1 className="text-2xl md:text-3xl font-medium text-center mt-2 mb-8 text-gray-800">
            {t('app.inputPrompt')}
          </h1>
        </div>
      )}

      {/* Área de error */}
      {error && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 px-4 py-3 rounded-xl bg-red-50 border border-red-300 text-red-700 max-w-md text-left shadow-md z-50 animate-fadeIn flex items-center">
          <span className="mx-1 flex-grow text-sm">{error}</span>
          <button 
            className="ml-2 text-red-500 hover:text-red-700 focus:outline-none flex-shrink-0"
            onClick={() => setError(null)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
      
      {/* Input siempre visible, con transición de posición */}
      <div 
        className={`transition-all duration-500 ease-in-out ${
          isInitialView 
            ? 'py-0' 
            : 'fixed bottom-0 left-0 right-0 py-4 bg-white shadow-md z-10'
        }`}
      >
        <URLInput 
          onSubmit={handleURLSubmit} 
          isLoading={isLoading}
          disabled={isLoading}
        />
      </div>
    </div>
  );
};

export default HomePage;